/**
 * @description Client SoT for X-Company-Id — All is never sent (LYN-357)
 *
 * @dependencies none
 *
 * @notes Mirrors backend/utilities/companyScope.js. Views must not slug-check.
 *   Live scope is sessionStorage (same-tab reload). Star is users.default_*.
 */

const COMPANY_ID_UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * @param {unknown} value
 * @returns {boolean}
 */
export function isSendableCompanyId(value) {
  if (value == null || value === '') {
    return false;
  }
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'all' || normalized === '*') {
    return false;
  }
  return COMPANY_ID_UUID.test(String(value).trim());
}

/**
 * /auth/me query for login vs live-session refresh.
 * default_* wins only when neither scope=all nor companyId is sent (login).
 *
 * @param {object} input
 * @param {unknown} [input.companyId]
 * @param {string|null} [input.scope]
 * @param {boolean} [input.refresh]
 * @param {unknown} [input.leftoverCompanyId]
 * @param {boolean} [input.useLeftover]
 * @returns {string}
 */
export function buildAuthMeSearch({
  companyId = null,
  scope = null,
  refresh = false,
  leftoverCompanyId = null,
  useLeftover = false
} = {}) {
  if (scope === 'all') {
    return 'scope=all';
  }
  if (isSendableCompanyId(companyId)) {
    return `companyId=${encodeURIComponent(String(companyId).trim())}`;
  }
  if (refresh) {
    return '';
  }
  if (useLeftover && isSendableCompanyId(leftoverCompanyId)) {
    return `companyId=${encodeURIComponent(String(leftoverCompanyId).trim())}`;
  }
  return '';
}

/** Same-tab live scope. Survives reload; empty on a new tab. Not the star. */
export const LIVE_COMPANY_SCOPE_KEY = 'liveCompanyScope';

/**
 * @param {Storage} [storage]
 * @returns {{ scope: 'all'|'company'|null, companyId: string|null }}
 */
export function readLiveCompanyScope(storage) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (!store) {
    return { scope: null, companyId: null };
  }
  const raw = store.getItem(LIVE_COMPANY_SCOPE_KEY);
  if (raw === 'all') {
    return { scope: 'all', companyId: null };
  }
  if (isSendableCompanyId(raw)) {
    return { scope: 'company', companyId: String(raw).trim() };
  }
  return { scope: null, companyId: null };
}

/**
 * Persist live switch for same-tab reload. Star / default_* is a different write.
 *
 * @param {{ scope?: string|null, companyId?: unknown }} input
 * @param {Storage} [storage]
 */
export function writeLiveCompanyScope({ scope = null, companyId = null } = {}, storage) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (!store) {
    return;
  }
  if (scope === 'all') {
    store.setItem(LIVE_COMPANY_SCOPE_KEY, 'all');
    return;
  }
  if (isSendableCompanyId(companyId)) {
    store.setItem(LIVE_COMPANY_SCOPE_KEY, String(companyId).trim());
    return;
  }
  store.removeItem(LIVE_COMPANY_SCOPE_KEY);
}

/**
 * New login / logout — next /me must apply default_*.
 *
 * @param {Storage} [storage]
 */
export function clearLiveCompanyScope(storage) {
  const store = storage || (typeof sessionStorage !== 'undefined' ? sessionStorage : null);
  if (!store) {
    return;
  }
  store.removeItem(LIVE_COMPANY_SCOPE_KEY);
}

export default isSendableCompanyId;
